<template>
  <v-container class="ma-0 pa-0 mw-100">
    <v-form ref="form" @keyup.native.enter="submit">
      <validation-observer ref="observer" v-slot="{ invalid }">
        <v-row class="pt-5">
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.StreetName')"
              rules="required|name_validate"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.StreetName')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.street_name"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.StreetNumber')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.StreetNumber')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.street_number"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.AdditionalLine')"
            >
              <v-text-field
                class="text-input"
                filled
                :label="$t('AdminPage.AdditionalLine')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.additional_line"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.ZipCode')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.ZipCode')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.zip_code"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.CityName')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.CityName')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.city"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.Country')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.Country')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.country"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <validation-provider
              :name="$t('AdminPage.AddressTyp')"
              rules="required|text"
            >
              <v-select
                filled
                :label="$t('AdminPage.AddressTyp')"
                :rules="[rules.required]"
                v-model="addresses.address_type"
                :items="types"
                prepend-inner-icon="mdi-pencil"
                required
              />
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.ContactPerson')"
              rules="required|text"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('AdminPage.ContactPerson')"
                prepend-inner-icon="mdi-pencil"
                v-model="addresses.contact_person"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col class="text-right">
            <custom-button
              @click="close"
              :text="$t('Headers.cancel')"
            ></custom-button>
            <custom-button
              :disabled="invalid"
              @click="submit"
              :text="$t('Headers.save')"
            ></custom-button>
          </v-col>
        </v-row>
      </validation-observer>
    </v-form>
  </v-container>
</template>

<script>
import i18n from "@/i18n";
import CustomButton from "../../components/ui/input/CustomButton";
import {
  extend,
  ValidationProvider,
  setInteractionMode,
  ValidationObserver,
} from "vee-validate";
import { required, email } from "vee-validate/dist/rules";
import { log } from "@/helpers/logger";

setInteractionMode("eager");

/**
 * @vue-prop {String} title - dialog title
 * @vue-prop {Object} data - stores the dialog data
 * @vue-data {Dictionary} user - stores the user
 * @vue-data {Dictionary} user_reset - stores the start data
 * @vue-data {Array} licenses - stores the selectable licenses
 * @vue-data {Array} roles - stores the selectable roles
 * @vue-data {Boolean}[loading=false] loading - gives, if the content is loading
 * @vue-data {Boolean} rules - checks, if field is required
 * @vue-event {} submit() - saves the user data to DB
 * @vue-event {} setUser() - saves data in the user variable
 * @vue-event {} getData() - gets license and role data from DB
 * @vue-event {} close() - sends close signal to vuex store
 */

extend("required", {
  ...required,
  message: i18n.t("FormValidation.required"),
});

extend("email", {
  ...email,
  message: i18n.t("FormValidation.email"),
});

extend("name_validate", {
  validate: (value) =>
    /(^[a-zA-ZäÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.\s]+)$/.test(
      value
    ),
  message: i18n.t("FormValidation.name"),
});

extend("text", {
  validate: (value) =>
    /(^[a-zA-Z0-9äÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.&\s]+)$/.test(
      value
    ),
  message: i18n.t("FormValidation.field"),
});

export default {
  name: "orga-dialog",
  components: {
    CustomButton,
    ValidationProvider,
    ValidationObserver,
  },
  title: i18n.t("HomePage.header.admin"),
  props: {
    title: String,
    addresses: Object,
    newAddress: Boolean,
  },
  data() {
    return {
      licenses: [],
      color: "",
      rules: {
        required: (value) => !!value || this.$t("Rules.required"),
      },
      types: [
        {
          value: "billing address",
          text: this.$t("AdminPage.BillingAddress"),
        },
        {
          value: "shipping address",
          text: this.$t("AdminPage.ShippingAddress"),
        },
      ],
    };
  },
  created() {
    this.color = this.$vuetify.theme.themes.light.primary;
  },
  methods: {
    submit() {
      var address;
      if (this.newAddress) {
        this.addresses.organization_id =
          this.$store.state.auth.user.organization_id;
        address = { address: this.addresses };
        this.$store
          .dispatch("data/addAddress", address)
          .then((res) => {
            log(
              "AdminPage.OrgaDialog.submit() - add address to organization " +
                this.$$store.state.auth.user.organization_id,
              "info"
            );
            this.data = res;
            location.reload();
            this.close();
          })
          .catch((err) => {
            log("AdminPage.OrgaDialog.submit() - " + err, "error");
          });
      } else {
        address = { address: this.addresses };
        this.$store
          .dispatch("data/updateAddress", address)
          .then((res) => {
            log(
              "AdminPage.OrgaDialog.submit() - update address " + addrees,
              "info"
            );
            this.data = res;
            location.reload();
            this.close();
          })
          .catch((err) => {
            log("AdminPage.OrgaDialog.submit() - " + err, "error");
          });
      }
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>
